import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { keepPreviousData, QueryClient, queryOptions, UndefinedInitialDataOptions } from '@tanstack/angular-query-experimental';
import { lastValueFrom, Observable } from 'rxjs';

import { CoreApiService } from '../core-api.service';
import { ProfileService } from '../profile/profile.service';
import { Group } from '../shared/models/group.model';
import { Paginated } from '../shared/models/paginated.model';
import { Project, ProjectWithStats } from '../shared/models/project.model';
import { Role } from '../shared/models/role.model';
import { User } from '../shared/models/user.model';
import { isContainerId } from '../shared/string.utils';

@Injectable({
  providedIn: 'root',
})
export class ProjectsQueryService {

  private coreApi = inject(CoreApiService);
  private profileService = inject(ProfileService);
  private exhaustiveMode = toSignal(this.profileService.exhaustiveMode$);
  private queryClient = inject(QueryClient);

  paginatedProjects(page: Signal<number>, sort?: Signal<string>, filter?: Signal<string>): UndefinedInitialDataOptions<Paginated<Project[]>> {

    const queryKey = ['projects', `page:${page()}`];
    if (sort?.()) {
      queryKey.push(sort());
    }

    if (filter?.()) {
      queryKey.push(filter());
    }

    return queryOptions({
      queryKey: queryKey,
      placeholderData: keepPreviousData,
      staleTime: 100000,
      meta: {
        invalidateOnExhaustiveMode: true,
      },
      queryFn: () => (
        lastValueFrom(
          this.coreApi.projects.query({
            exhaustive: this.exhaustiveMode(),
            limit: 15,
            page: page(),
            join_avatars: true,
            filter: filter?.(),
            sort: sort?.(),
          })
        )
      ),
    })
  }

  allLockedProjects(): UndefinedInitialDataOptions<Paginated<Project[]>> {
    return queryOptions({
      queryKey: ['projects', 'all', 'locked'],
      placeholderData: keepPreviousData,
      staleTime: 100000,
      meta: {
        invalidateOnExhaustiveMode: true,
      },
      queryFn: () => (
        // FIXME: Wrap in queryAll
        lastValueFrom(
          this.coreApi.projects.query({
            exhaustive: this.exhaustiveMode(),
            filter: 'locked!=null',
          })
        )
      ),
    })
  }

  allProjectRoles(): UndefinedInitialDataOptions<Paginated<Role[]>> {
    return queryOptions({
      queryKey: ['projectRoles'],
      queryFn: () => (
        lastValueFrom(
          this.coreApi.roles.query({
            exhaustive: true,
          }),
        )
      ),
    })
  }

  private getCachedProjects(): Project[] {
    const prefetchedQueries = this.queryClient.getQueriesData<Paginated<Project[]>>({
      predicate: query => query.queryKey[0] === 'projects' && query.queryKey[1] === 'page',
    });
    const projects = prefetchedQueries?.flatMap(([, projects]) => projects.results);
    return projects || [];
  }


  getProject(projectId: Signal<string>): UndefinedInitialDataOptions<Project> {
    return queryOptions({
      queryKey: ['project', 'id', projectId()],
      queryFn: () => (
        lastValueFrom(
          this.coreApi.projects.get(projectId())
        )
      ),
      enabled: () => isContainerId(projectId()),
      initialData: () => {
        const cachedProjects = this.getCachedProjects();
        const project = cachedProjects.find(p => p._id === projectId());
        return project || undefined;
      },
    })
  }

  allUsers(): UndefinedInitialDataOptions<Paginated<User[]>> {
    return queryOptions({
      queryKey: ['users'],
      queryFn: () => (
        // FIXME: Wrap in queryAll
        lastValueFrom(
          this.coreApi.users.query()
        )
      ),
    })
  }

  allGroups(): UndefinedInitialDataOptions<Paginated<Group[]>> {
    return queryOptions({
      queryKey: ['groups'],
      queryFn: () => (
        lastValueFrom(
          this.coreApi.groups.query({ exhaustive: this.exhaustiveMode() })
        )
      ),
      meta: {
        invalidateOnExhaustiveMode: true,
      },
    })
  }

  groupById(groupId: Signal<string>): UndefinedInitialDataOptions<Group> {
    return queryOptions({
      queryKey: ['group', groupId()],
      queryFn: () => (
        lastValueFrom(
          this.coreApi.groups.get(groupId())
        )
      ),
    })
  }

  getProjectWithStats(projectId: Signal<string>): UndefinedInitialDataOptions<ProjectWithStats> {
    return queryOptions({
      queryKey: ['project', projectId(), 'stats'],
      queryFn: () => (
        lastValueFrom(
          this.coreApi.projects.get(projectId(), { stats: true }) as Observable<ProjectWithStats>
        )
      ),
    })
  }

  invalidateProjectCache(projectId: string): void {
    this.queryClient.invalidateQueries({
      queryKey: ['project', 'id', projectId],
    });
    // Also invalidate the paginated projects lists since we have no way of knowing which page the project was on
    this.invalidateProjectListCache();
  }

  invalidateProjectListCache(): void {
    this.queryClient.invalidateQueries({
      predicate: query =>
        query.queryKey[0] === 'projects' && String(query.queryKey[1]).startsWith('page:'),
    });
  }
}
